'use client';

import { c as _c } from "react/compiler-runtime";
import type { InboxAutoFixSummary } from '@/app/(home)/inbox/api/pull-requests/auto-fix-summaries';
import type { InboxPullRequest } from '@/app/(home)/inbox/types';
import type { CubicReviewerPresentation } from '@/app/(home)/inbox/utils/cubic-reviewer-presentation';
import type { AutoFixLoopActivity, AutoFixLoopPresentation } from '@/lib/auto-fix-loop/presentation';
import { Check, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';
import { createContext, use } from 'react';
import { getCubicReviewerPresentation, getCubicReviewerStatusLabel } from '@/app/(home)/inbox/utils/cubic-reviewer-presentation';
import { Logo } from '@/components/icons';
import { REVIEWER_BADGE_CLASS } from '@/components/reviewers/reviewer-badge';
import { BetterTooltip } from '@/components/ui/tooltip';
import { useInboxV2AutoFixSummaries } from '@/data/pull-requests/use-inbox-v2-auto-fix-summaries';
import { describeAutoFixLoop, resolveAutoFixLoopActivity } from '@/lib/auto-fix-loop/presentation';
import { getInboxCheckSummaryKey } from '@/lib/pr-v2/check-run-presentation';
const InboxAutoFixSummariesContext = createContext<Readonly<Record<string, InboxAutoFixSummary>>>({});
const EMPTY_SUMMARIES: Readonly<Record<string, InboxAutoFixSummary>> = {};

/**
 * Loads the open rows' auto-fix summaries beside the inbox list and hands them
 * to every row, stack members included, through context.
 */
export function InboxAutoFixSummariesProvider(t0) {
  const $ = _c(5);
  const {
    refetchOnMount,
    children
  } = t0;
  let t1;
  if ($[0] !== refetchOnMount) {
    t1 = {
      refetchOnMount
    };
    $[0] = refetchOnMount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data
  } = useInboxV2AutoFixSummaries(t1);
  const t2 = data ?? EMPTY_SUMMARIES;
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <InboxAutoFixSummariesContext value={t2}>{children}</InboxAutoFixSummariesContext>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
export type InboxAutoFixLoop = AutoFixLoopPresentation & {
  activity: AutoFixLoopActivity;
};
function getInboxAutoFixLoop(pr: InboxPullRequest, summaries: Readonly<Record<string, InboxAutoFixSummary>>): InboxAutoFixLoop | null {
  const summary = summaries[getInboxCheckSummaryKey({
    owner: pr.repository.owner,
    repo: pr.repository.name,
    number: pr.number
  })];
  if (!summary) {
    return null;
  }
  const activity = resolveAutoFixLoopActivity({
    status: summary.status,
    runningFindingCount: summary.runningFindingCount,
    runningOpenFindingCount: summary.runningOpenFindingCount,
    runningProcessedCount: summary.runningProcessedCount,
    runningCheckCount: summary.runningCheckCount,
    failingCheckCount: summary.failingCheckCount,
    cubicReviewRunning: getCubicReviewerPresentation(pr.latestCubicReview).kind === 'running',
    openIssueCount: pr.latestCubicReview?.unresolvedCommentCount
  });
  return {
    activity,
    ...describeAutoFixLoop(activity)
  };
}

/**
 * A row's auto-fix loop: where it stands, with its sentence and phase, or null
 * while auto-fix is off or still loading. cubic counts as reviewing when its
 * review is running, the same test the slot uses.
 */
export function useInboxAutoFixLoop(pr: InboxPullRequest): InboxAutoFixLoop | null {
  return getInboxAutoFixLoop(pr, use(InboxAutoFixSummariesContext));
}

/** Reads any row's auto-fix loop as {@link useInboxAutoFixLoop} does, for code that sorts many rows. */
export function useInboxAutoFixLoopReader(): (pr: InboxPullRequest) => InboxAutoFixLoop | null {
  const summaries = use(InboxAutoFixSummariesContext);
  return pr => getInboxAutoFixLoop(pr, summaries);
}
export type CubicBadgeKind = 'fixing' | 'reviewing' | 'failed';

/**
 * The badge beside cubic's logo, most pressing first: a run fixing, a
 * review in progress, then a failure of either. `phase` keeps "!" in step with
 * the tray row's "Auto-fix:" reason. Null leaves the slot to cubic's open issues.
 */
export function getCubicBadge(review: CubicReviewerPresentation, autoFix: InboxAutoFixLoop | null): CubicBadgeKind | null {
  if (autoFix?.activity.kind === 'fixing') {
    return 'fixing';
  }
  if (review.kind === 'running') {
    return 'reviewing';
  }
  if (review.kind === 'failed' || autoFix?.phase === 'failed') {
    return 'failed';
  }
  return null;
}

/** A quarter arc spinning on the badge's ring: cubic is busy. */
const SPINNING_RING = <span className="pointer-events-none absolute -inset-px rounded-full border border-transparent border-t-zinc-200 motion-safe:animate-spin" />;

/**
 * Badge contents. While cubic is busy the ring spins and the inside says with
 * what: empty for a review, the spanner, held still, for a fix. A failure is a
 * bare "!", which lucide only ships inside a shape.
 */
const CUBIC_BADGE_ICONS: Record<CubicBadgeKind, ReactNode> = {
  reviewing: SPINNING_RING,
  fixing: <>
      <Wrench className="size-2 text-zinc-100" />
      {SPINNING_RING}
    </>,
  failed: <svg viewBox="0 0 24 24" className="size-2 text-zinc-300" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round">
      <path d="M12 4.5v9.5" />
      <path d="M12 19.5h.01" />
    </svg>
};

/** cubic's logo with its corner badge, if any, in the reviewer avatars' style. */
export function CubicMark(t0) {
  const $ = _c(5);
  const {
    badge
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Logo size={16} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== badge) {
    t2 = badge && <span className={REVIEWER_BADGE_CLASS} aria-hidden="true" data-badge={badge}>{CUBIC_BADGE_ICONS[badge]}</span>;
    $[1] = badge;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="relative inline-flex size-5 shrink-0 items-center justify-center">{t1}{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * cubic's segment on an inbox row's meta line: its logo, then a badge while it
 * reviews, fixes, or failed, and otherwise its open issues as a number. Blank
 * when cubic has nothing for the viewer: no review yet, a clean review, or
 * auto-fix done or waiting with nothing open; whether auto-fix is on stays in
 * the PR sidebar. Grey like the rest of the line. The tooltip gives cubic's
 * review status and the auto-fix loop's.
 */
export function PrInboxCubicStatus(t0) {
  const $ = _c(5);
  const {
    pr
  } = t0;
  let t1;
  if ($[0] !== pr.latestCubicReview) {
    t1 = getCubicReviewerPresentation(pr.latestCubicReview);
    $[0] = pr.latestCubicReview;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const review = t1;
  const autoFix = useInboxAutoFixLoop(pr);
  let t2;
  if ($[2] !== autoFix || $[3] !== review) {
    t2 = <PrInboxCubicStatusView review={review} autoFix={autoFix} />;
    $[2] = autoFix;
    $[3] = review;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * cubic's slot drawn from its review and auto-fix loop; null `autoFix` leaves the loop out.
 * `showClean` puts a check after the logo for a clean review, for a card about one pull request,
 * where a blank slot cannot tell a clean review from no review at all. The inbox leaves it off,
 * since a row's slot draws only what needs a look.
 */
export function PrInboxCubicStatusView(t0) {
  const $ = _c(23);
  const {
    review,
    autoFix,
    showClean: t1
  } = t0;
  const showClean = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== autoFix || $[1] !== review) {
    t2 = getCubicBadge(review, autoFix);
    $[0] = autoFix;
    $[1] = review;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const badge = t2;
  const count = !badge && review.kind === "issues" ? review.displayCount : null;
  const clean = showClean && !badge && review.kind === "clean";
  if (!badge && !count && !clean) {
    return null;
  }
  let t3;
  if ($[3] !== review) {
    t3 = getCubicReviewerStatusLabel(review);
    $[3] = review;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const reviewLabel = t3;
  const t4 = reviewLabel && `cubic: ${reviewLabel}`;
  const t5 = autoFix && `Auto-fix: ${autoFix.description}`;
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = [t4, t5].filter(Boolean);
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const label = t6.join(" \xB7 ");
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Logo size={13} className="rounded-[3px] opacity-70" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== badge) {
    t8 = badge && <span className="relative inline-flex size-3 items-center justify-center rounded-full ring-1 ring-zinc-700" data-badge={badge}>{CUBIC_BADGE_ICONS[badge]}</span>;
    $[9] = badge;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== clean) {
    t9 = clean && <Check aria-hidden="true" className="size-3" strokeWidth={2.4} />;
    $[11] = clean;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== count || $[14] !== t8 || $[15] !== t9) {
    t10 = <span className="inline-flex items-center gap-[5px] align-[-2px]">{t7}{t8}{t9}{count}</span>;
    $[13] = count;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== label || $[18] !== t10) {
    t11 = <span role="img" aria-label={label} className="tabular-nums text-zinc-400" data-testid="pr-inbox-cubic-status">{t10}</span>;
    $[17] = label;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== label || $[21] !== t11) {
    t12 = <BetterTooltip content={label}>{t11}</BetterTooltip>;
    $[20] = label;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}